import React from "react";
import { AlertTriangle } from "lucide-react";
import ReportShell from "@/components/ReportShell";
import { Field, TextInput, TextArea, Select } from "@/components/ReportPrimitives";
import { buildBopTestReport, bopReviewChecklist, BOP_QUICK_PHRASES } from "@/lib/reports/bopTest";
import { bopStore } from "@/lib/reports/storage";
import { useReportRceWiring } from "@/lib/rce/useReportRceWiring";

const emptyState = () => ({
  id: null, header: {}, stack: {}, equipment: {}, lowPressure: {}, highPressure: {}, duration: {}, bleedBack: {}, leaks: {}, witnesses: {}, approvals: {},
});

function QuickPhrases({ onPick, testId }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {BOP_QUICK_PHRASES.map((p) => (
        <button key={p} type="button" onClick={() => onPick(p)} className="text-[10.5px] px-2 py-1 rounded-md" style={{ background: "rgba(22,24,29,0.6)", color: "var(--rs-text-2)", border: "1px solid var(--rs-border)" }}>+ {p}</button>
      ))}
    </div>
  );
}

// Prominent "Recorded only — final verification" reminder banner.
const RecordedOnlyNotice = ({ testId }) => (
  <div
    data-testid={testId}
    className="card-rs p-3 flex items-start gap-2.5"
    style={{ background: "rgba(244,67,54,0.07)", borderColor: "#C62828" }}
  >
    <AlertTriangle className="w-3.5 h-3.5 mt-[3px] shrink-0" style={{ color: "var(--rs-danger)" }} />
    <p className="text-[11px] leading-snug text-[#FCA5A5]">
      <span className="font-semibold" style={{ color: "var(--rs-danger)" }}>Recorded status only.</span>{" "}
      Final verification must be performed by competent personnel under approved procedures, manufacturer requirements, client requirements, and applicable regulations. This tool does not certify pass / fail.
    </p>
  </div>
);

export default function BopTestSheet() {
  const rce = useReportRceWiring("bopTestSheet");
  const sections = [
    { id: "header", badge: "01", title: "Report Header", defaultOpen: true, render: (state, set) => (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Report Date"><TextInput type="date" value={state.header.reportDate} onChange={(v) => set("header","reportDate",v)} testId="bop-h-reportDate" /></Field>
        <Field label="Rig Name"><TextInput value={state.header.rigName} onChange={(v) => set("header","rigName",v)} testId="bop-h-rigName" /></Field>
        <Field label="Well Name"><TextInput value={state.header.wellName} onChange={(v) => set("header","wellName",v)} testId="bop-h-wellName" /></Field>
        <Field label="Operator / Client"><TextInput value={state.header.operator} onChange={(v) => set("header","operator",v)} testId="bop-h-operator" /></Field>
        <Field label="Prepared By"><TextInput value={state.header.preparedBy} onChange={(v) => set("header","preparedBy",v)} testId="bop-h-preparedBy" /></Field>
        <Field label="Test Reason"><Select value={state.header.testReason} onChange={(v) => set("header","testReason",v)} options={["Initial Installation","Periodic","Repair","Pre-Spud","After Casing","Other"]} testId="bop-h-testReason" /></Field>
      </div>
    )},
    { id: "stack", badge: "02", title: "Rig / Well / Stack Information", render: (state, set) => (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Stack Type"><Select value={state.stack.stackType} onChange={(v) => set("stack","stackType",v)} options={["Surface","Subsea"]} testId="bop-st-stackType" /></Field>
        <Field label="WP Rating"><TextInput value={state.stack.wpRating} onChange={(v) => set("stack","wpRating",v)} testId="bop-st-wpRating" /></Field>
        <Field label="Surface or Subsea"><TextInput value={state.stack.surfaceOrSubsea} onChange={(v) => set("stack","surfaceOrSubsea",v)} testId="bop-st-surfaceOrSubsea" /></Field>
        <Field label="BOP Serial"><TextInput value={state.stack.bopSerial} onChange={(v) => set("stack","bopSerial",v)} testId="bop-st-bopSerial" /></Field>
        <Field label="Last Major Inspection"><TextInput value={state.stack.lastInspection} onChange={(v) => set("stack","lastInspection",v)} testId="bop-st-lastInspection" /></Field>
      </div>
    )},
    { id: "referenceStack", badge: "02b", title: "Reference Stack (optional)", render: (state, set, helpers) => (
      <BOPStackPicker
        onApply={(payload) => {
          helpers.setState((s) => ({
            ...s,
            stack: { ...s.stack, ...payload.stack },
            equipment: { ...s.equipment, ...payload.equipment },
            referenceProvenance: payload.provenance,
          }));
        }}
      />
    )},
    { id: "equipment", badge: "03", title: "Equipment / Component List", render: (state, set) => (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[
          ["annular","Annular Preventer"],
          ["upperPipeRams","Upper Pipe Rams"],
          ["middlePipeRams","Middle Pipe Rams"],
          ["lowerPipeRams","Lower Pipe Rams"],
          ["vbr","Variable Bore Rams"],
          ["blindShearRams","Blind / Shear Rams"],
          ["chokeValves","Choke Valve(s)"],
          ["killValves","Kill Valve(s)"],
          ["chokeManifold","Choke Manifold"],
          ["accumulator","Accumulator / Control System"],
        ].map(([k, label]) => (
          <Field key={k} label={label}><TextInput value={state.equipment[k]} onChange={(v) => set("equipment",k,v)} testId={`bop-eq-${k}`} /></Field>
        ))}
      </div>
    )},
    { id: "lowPressure", badge: "04", title: "Low-Pressure Test", render: (state, set) => (
      <>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Target Low Pressure"><TextInput value={state.lowPressure.target} onChange={(v) => set("lowPressure","target",v)} testId="bop-lp-target" /></Field>
          <Field label="Hold Time"><TextInput value={state.lowPressure.holdTime} onChange={(v) => set("lowPressure","holdTime",v)} testId="bop-lp-holdTime" /></Field>
          <Field label="Components Tested"><TextInput value={state.lowPressure.componentsTested} onChange={(v) => set("lowPressure","componentsTested",v)} testId="bop-lp-componentsTested" /></Field>
          <Field label="Result (as entered)" helper="Enter the result as recorded by the witness. This tool does not certify pass/fail."><TextInput value={state.lowPressure.result} onChange={(v) => set("lowPressure","result",v)} testId="bop-lp-result" /></Field>
        </div>
        <Field label="Notes"><TextArea value={state.lowPressure.notes} onChange={(v) => set("lowPressure","notes",v)} rows={2} testId="bop-lp-notes" /></Field>
      </>
    )},
    { id: "highPressure", badge: "05", title: "High-Pressure Test", render: (state, set) => (
      <>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Target High Pressure"><TextInput value={state.highPressure.target} onChange={(v) => set("highPressure","target",v)} testId="bop-hp-target" /></Field>
          <Field label="Hold Time"><TextInput value={state.highPressure.holdTime} onChange={(v) => set("highPressure","holdTime",v)} testId="bop-hp-holdTime" /></Field>
          <Field label="Components Tested"><TextInput value={state.highPressure.componentsTested} onChange={(v) => set("highPressure","componentsTested",v)} testId="bop-hp-componentsTested" /></Field>
          <Field label="Result (as entered)" helper="Enter the result as recorded by the witness. This tool does not certify pass/fail."><TextInput value={state.highPressure.result} onChange={(v) => set("highPressure","result",v)} testId="bop-hp-result" /></Field>
        </div>
        <Field label="Notes"><TextArea value={state.highPressure.notes} onChange={(v) => set("highPressure","notes",v)} rows={2} testId="bop-hp-notes" /></Field>
      </>
    )},
    { id: "duration", badge: "06", title: "Test Duration / Hold Period", render: (state, set) => (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Total Test Duration"><TextInput value={state.duration.total} onChange={(v) => set("duration","total",v)} testId="bop-dur-total" /></Field>
        <Field label="Hold Period Per Component"><TextInput value={state.duration.perComponent} onChange={(v) => set("duration","perComponent",v)} testId="bop-dur-perComponent" /></Field>
      </div>
    )},
    { id: "bleedBack", badge: "07", title: "Bleed-Back Notes", render: (state, set) => (
      <Field label="Bleed-Back Notes"><TextArea value={state.bleedBack.notes} onChange={(v) => set("bleedBack","notes",v)} rows={3} testId="bop-bb-notes" /></Field>
    )},
    { id: "leaks", badge: "08", title: "Leaks / Observations", render: (state, set, helpers) => (
      <>
        <Field label="Observations (as recorded)"><TextArea value={state.leaks.observations} onChange={(v) => set("leaks","observations",v)} rows={3} testId="bop-leaks-observations" /></Field>
        <QuickPhrases onPick={(p) => helpers.setState((s) => ({ ...s, leaks: { ...s.leaks, observations: ((s.leaks.observations || "") + (s.leaks.observations && !/\s$/.test(s.leaks.observations || "") ? " " : "") + p) } }))} testId="bop-leaks-quick-phrases" />
      </>
    )},
    { id: "witnesses", badge: "09", title: "Witness / Supervisor / Company Representative", render: (state, set) => (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Witness (as entered)"><TextInput value={state.witnesses.witness} onChange={(v) => set("witnesses","witness",v)} testId="bop-w-witness" /></Field>
        <Field label="Toolpusher / Supervisor (as entered)"><TextInput value={state.witnesses.supervisor} onChange={(v) => set("witnesses","supervisor",v)} testId="bop-w-supervisor" /></Field>
        <Field label="Company Representative (as entered)"><TextInput value={state.witnesses.companyRep} onChange={(v) => set("witnesses","companyRep",v)} testId="bop-w-companyRep" /></Field>
        <Field label="Service Company Rep (as entered)"><TextInput value={state.witnesses.serviceRep} onChange={(v) => set("witnesses","serviceRep",v)} testId="bop-w-serviceRep" /></Field>
      </div>
    )},
  ];

  return (
    <ReportShell
      testIdPrefix="bop"
      reportType="bopTestSheet"
      safetyCritical={true}
      title="BOP Test Sheet"
      subtitle="Records BOP test data as user-entered values. Does not certify pass / fail."
      brandLine="BOP Test Sheet  ·  Draft Documentation Support"
      emptyState={emptyState}
      buildFn={buildBopTestReport}
      checklistFn={bopReviewChecklist}
      store={bopStore}
      sections={sections}
      initialState={rce.initialState}
      rceContextStrip={rce.rceContextStrip}
      topNotice={<RecordedOnlyNotice testId="bop-recorded-only-top" />}
      bottomNotice={<RecordedOnlyNotice testId="bop-recorded-only-bottom" />}
      filenameFromState={(s) => {
        const safe = (v) => (v || "").toString().replace(/[^a-z0-9-_]+/gi, "_").slice(0, 30);
        return `RigSentinel_BOPTest_${safe(s.header.wellName) || "well"}_${(s.header.reportDate || "").replace(/[^0-9]/g, "") || Date.now()}.pdf`;
      }}
    />
  );
}
